import { Link as ScrollLink } from 'react-scroll';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logoViso from '../assets/VISO Analytics 1.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll state for glass + shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { name: 'Home', to: 'home' },
    { name: 'About', to: 'about' },
    { name: 'Services', to: 'services' },
    { name: 'Contact', to: 'contact' }
  ];

  return (
    <motion.nav
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-white/70 backdrop-blur-xl ring-1 ring-blue-900/5 shadow-sm' : 'bg-transparent'
      }`}
      aria-label="Primary"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between">
          {/* Brand */}
          <ScrollLink
            to="home"
            smooth
            duration={500}
            className="flex items-center gap-3 cursor-pointer select-none"
          >
            <img src={logoViso} alt="VISO Analytics" className="h-16 w-auto" />
            <span className="sr-only">VISO Analytics</span>
          </ScrollLink>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <ScrollLink
                key={link.name}
                to={link.to}
                smooth
                duration={500}
                offset={-80}
                spy
                activeClass="text-blue-700 after:scale-x-100"
                className="relative px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors
                           after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-blue-600 after:origin-left after:scale-x-0 after:transition-transform after:duration-300"
              >
                {link.name}
              </ScrollLink>
            ))}

            {/* CTA */}
            <ScrollLink
              to="contact"
              smooth
              duration={500}
              offset={-80}
              className="ml-2 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
            >
              Contact
            </ScrollLink>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
              onClick={() => setIsOpen((v) => !v)}
              className="p-2 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
            >
              <span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
              <div className="relative h-5 w-6">
                <span className={`absolute left-0 top-0 h-0.5 w-6 bg-blue-700 transition-transform duration-300 ${isOpen ? 'translate-y-2 rotate-45' : ''}`} />
                <span className={`absolute left-0 top-2 h-0.5 w-6 bg-blue-700 transition-opacity duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`absolute left-0 top-4 h-0.5 w-6 bg-blue-700 transition-transform duration-300 ${isOpen ? '-translate-y-2 -rotate-45' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="md:hidden overflow-hidden"
          >
            <div className="mx-4 mb-4 rounded-lg border border-blue-900/5 bg-white/90 backdrop-blur-xl shadow-lg">
              <nav className="px-3 py-3 space-y-1">
                {navLinks.map((link) => (
                  <ScrollLink
                    key={link.name}
                    to={link.to}
                    smooth
                    duration={500}
                    offset={-70}
                    spy
                    activeClass="text-blue-700 bg-blue-50"
                    className="block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </ScrollLink>
                ))}
                <ScrollLink
                  to="contact"
                  smooth
                  duration={500}
                  offset={-70}
                  className="mt-2 block rounded-md bg-blue-600 px-3 py-2 text-center text-base font-medium text-white hover:bg-blue-700"
                  onClick={() => setIsOpen(false)}
                >
                  Contact
                </ScrollLink>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;